import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format, differenceInHours, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarDays, Clock, XCircle, ChevronRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PlayerSessionDetail from "./PlayerSessionDetail";

const CANCELLATION_HOURS = 24;

export default function PlayerTrainingSessions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const { data: attendance = [], isLoading } = useQuery({
    queryKey: ["player-training-sessions", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("session_attendance")
        .select(
          "id, rsvp_status, cancelled_at, session_id, training_sessions!inner(id, session_date, start_time, duration_minutes, status, summary, focus_areas, group_id, training_groups(name, color), courts(name))"
        )
        .eq("player_id", user!.id)
        .gte("training_sessions.session_date", today)
        .order("created_at", { ascending: true });
      if (error) throw error;
      // Sort by session_date on client
      return (data ?? []).sort((a: any, b: any) => {
        const dA = a.training_sessions?.session_date ?? "";
        const dB = b.training_sessions?.session_date ?? "";
        return dA.localeCompare(dB);
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (attendanceId: string) => {
      const { error } = await supabase
        .from("session_attendance")
        .update({
          rsvp_status: "not_attending",
          cancelled_by: user!.id,
          cancelled_at: new Date().toISOString(),
        })
        .eq("id", attendanceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-training-sessions"] });
      toast({ title: "Avanmälan registrerad" });
    },
    onError: () => toast({ title: "Kunde inte avanmäla", variant: "destructive" }),
  });

  const canCancel = (sessionDate: string, startTime: string) => {
    const dt = parseISO(`${sessionDate}T${startTime}`);
    return differenceInHours(dt, new Date()) >= CANCELLATION_HOURS;
  };

  if (selectedSessionId) {
    return (
      <PlayerSessionDetail
        sessionId={selectedSessionId}
        onBack={() => setSelectedSessionId(null)}
      />
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Laddar pass...</p>;
  }

  if (attendance.length === 0) {
    return <p className="text-sm text-muted-foreground">Du har inga kommande träningspass.</p>;
  }

  return (
    <div className="space-y-3">
      {attendance.map((a: any) => {
        const s = a.training_sessions;
        if (!s) return null;
        const isCancelled = a.rsvp_status === "not_attending";
        const deadlineOk = canCancel(s.session_date, s.start_time);

        return (
          <Card
            key={a.id}
            className={`cursor-pointer transition-colors hover:bg-muted/50 ${isCancelled ? "opacity-60" : ""}`}
            onClick={() => setSelectedSessionId(s.id)}
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.training_groups?.color && (
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: s.training_groups.color }}
                      />
                    )}
                    <span className="font-medium text-sm">{s.training_groups?.name}</span>
                    <Badge variant={isCancelled ? "destructive" : "outline"} className="text-xs">
                      {isCancelled ? "Avanmäld" : "Anmäld"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {format(parseISO(s.session_date), "EEE d MMM", { locale: sv })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {s.start_time?.slice(0, 5)} ({s.duration_minutes} min)
                    </span>
                    {s.courts?.name && <span>{s.courts.name}</span>}
                  </div>
                  {s.focus_areas && s.focus_areas.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1">
                      {s.focus_areas.map((f: string) => (
                        <Badge key={f} variant="secondary" className="text-[10px] px-1.5 py-0">
                          {f}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isCancelled && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={(e) => e.stopPropagation()}
                          disabled={!deadlineOk}
                          title={deadlineOk ? "Avanmäl" : `Deadline passerad (${CANCELLATION_HOURS}h före)`}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Avanmäla dig?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Du kommer att avanmälas från passet{" "}
                            {format(parseISO(s.session_date), "d MMM", { locale: sv })} kl{" "}
                            {s.start_time?.slice(0, 5)}.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Avbryt</AlertDialogCancel>
                          <AlertDialogAction onClick={() => cancelMutation.mutate(a.id)}>
                            Avanmäl
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
